import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { Scoresheet } from '@exports/Scoresheet';
import { cx } from '@libs/classnames';

import { ErrorPage } from '@features/auth/pages/ErrorPage';
import { TIME_PRESSURE } from '@features/game/constants';
import { useFastGame } from '@features/game/hooks/useFastGame';
import { useFastGameCtx } from '@features/game/providers/FastGameProvider';
import { DrawOffer } from '@features/game/ui/DrawOffer';
import { GameFinishedModal } from '@features/game/ui/GameFinishedModal/GameFinishedModal';
import { GameInfo } from '@features/game/ui/GameInfo/GameInfo';
import { GameLayout, GameLayoutSkeleton } from '@features/game/ui/GameLayout/GameLayout';
import { OnlineGamesModalWidget } from '@features/game/ui/OnlineGamesModal/OnlineGamesModalWidget';
import { PlayerPanelWidget } from '@features/game/ui/PlayerPanel';
import { createStubFastGame } from '@features/game/utils/createStubFastGame';
import { getGamePgnFilename, mapGameToPgn } from '@features/game/utils/gamePgnUtils';
import { ServerGameWidget } from '@features/game/widgets/ServerGameWidget';
import { IValidRoute } from '@features/shared/utils/router';
import { UserAuthMenuWidget } from '@features/user/ui/UserAuthMenu/UserAuthMenuWidget';
import { UserSettingsMenuWidget } from '@features/user/ui/UserSettingsMenu/UserSettingsMenuWidget';

import { Analysis } from '../AnalysisPage';
import { GameActions } from './components/GameActions/GameActions';

import styles from './PairingGamePageWidget.module.css';

/*
    ____,-------------------------------,____
    \   |              Page             |   /
    /___|-------------------------------|___\
*/

type Props = {
  key: string /* sic! Для разных игр необходимы разные ключи */;
  gameId: string;
};

const BACK_LINK: IValidRoute = '/game/pairing';

export function PairingGamePageWidget({ gameId }: Props) {
  const mobile = useMediaQuery() !== 'desktop';
  const navigate = useNavigate();
  const fg = useFastGameCtx();
  const customBackUrl = useLocation().state.backUrl;

  const stubGame = useMemo(() => createStubFastGame(gameId), [gameId]);

  const {
    game,
    isGamePending,
    onRefetchGame,
    //
    isInProgress,
    isFinished,
    //
    side,
    playerSide,
    isPlayer,
    //
    rotated,
    boardOrientation,
    player1Color,
    player2Color,
    //
    toast,
    onError,
    //
    canRequestDraw,
    showDrawOffer,
    onDrawAccept,
    onDrawDecline,
    onDrawOffer,
    //
    canResign,
    onResign,
    //
    canClaimVictory,
    onClaimVictory,
    //
    isPlayer1Offline,
    isPlayer2Offline,
    onNetworkProblems,
    //
    rematchStatus,
    onRematch,
    //
    gameSpecs,
    //
    scoresheet,
    customPlayersTimeMs,
    historyMove,
    //
    arrows,
    onUserArrow,
    //
    modal,
    //
    canShowAnalysis,
    showAnalysis,
    onOpenAnalysis,
    onCloseAnalysis,
    //
    isTopFirstMove,
    isBottomFirstMove,
    //
    boardTheme,
    autoPromotion,
    boardSounds,
  } = useFastGame(
    fg.playerId,
    fg.isLoading ? fg.game : (fg.game ?? /* если это чужая игра: */ stubGame),
  );

  // ------------------------------------------
  // Other

  const [matchmakingOpen, setMatchmakingOpen] = useState(false);
  const showMatchmakingModal = matchmakingOpen && (!fg.game || fg.game.isFinished);

  const handleNewOpponent = useCallback(() => {
    const select = fg.select;
    if (fg.game?.usedPools?.length && !fg.selected.length) {
      select(fg.game.usedPools);
    }
    setMatchmakingOpen(true);
  }, [fg.select, fg.selected.length, fg.game?.usedPools]);

  const handleNewSearch = useCallback(() => {
    const reset = fg.reset;
    reset();
    setMatchmakingOpen(true);
  }, [fg.reset]);

  useEffect(() => {
    if (fg.selected.length) {
      setMatchmakingOpen(true);
    }
  }, [fg.selected.length]);

  const [finishedDismissed, setFinishedDismissed] = useState(false);

  const onClosePage = useCallback(() => {
    // sic! Не вызываем fg.reset() в этом обработчике
    // т.к. сброс пулов осуществляется при открытии родительского раздела

    // HACK: из-за неудачной реализации подразделов ЛК (закрываются недетерминированно по useGoBack),
    // безопасней использовать history.back()
    customBackUrl ? history.back() : navigate({ to: BACK_LINK });
  }, [navigate, customBackUrl]);

  if (fg.game?.customTokenError) {
    return <ErrorPage subTitle={'Не удалось подключиться к игре. Обновите страницу'} />;
  }

  if (fg.isLoading || fg.game?.isLoading || isGamePending || !game) {
    return <GameLayoutSkeleton />;
  }

  if (showAnalysis) {
    return (
      <Analysis
        avatar={
          <>
            <UserSettingsMenuWidget />
            <UserAuthMenuWidget alwaysRound />
          </>
        }
        twoAvatars
        initialPgn={mapGameToPgn(game)}
        filename={getGamePgnFilename(game)}
        initialOrientation={side}
        isRealGameAnalysis
        onClose={onCloseAnalysis}
      />
    );
  }

  const canFindNew = Boolean(fg.game?.usedPools?.length);

  const showFinishedModal =
    isFinished && canFindNew && !finishedDismissed && !showMatchmakingModal && !showAnalysis;

  return (
    <>
      {showFinishedModal && playerSide !== 'none' && (
        <GameFinishedModal
          game={game}
          side={playerSide}
          isMobile={mobile}
          position="centered"
          rematch={rematchStatus}
          onRematch={onRematch}
          onNewGame={handleNewOpponent}
          onAnalysis={canShowAnalysis ? onOpenAnalysis : undefined}
          onClose={() => setFinishedDismissed(true)}
        />
      )}
      {showMatchmakingModal && (
        <OnlineGamesModalWidget position="centered" onClose={() => setMatchmakingOpen(false)} />
      )}
      <GameLayout
        backLink={customBackUrl ?? BACK_LINK}
        onBackClick={onClosePage}
        board={
          <ServerGameWidget
            key={rotated ? 'r' : 'o'}
            className={cx({
              [styles.is_rotate_animated]: rotated !== null || showAnalysis !== null,
            })}
            mode={isPlayer && isInProgress ? 'fast-game' : 'spectator'}
            game={game}
            playerId={fg.playerId}
            anonToken={fg.game?.customToken}
            historyMove={historyMove}
            onError={onError}
            onRefetch={onRefetchGame}
            onNetworkProblems={onNetworkProblems}
            //
            allowPremove
            autoPromotion={autoPromotion}
            boardSounds={boardSounds}
            //
            orientation={boardOrientation}
            theme={boardTheme}
            notation={mobile ? 'inside' : undefined}
            modal={modal}
            arrows={arrows}
            onUserArrow={onUserArrow}
          />
        }
        avatar={
          <>
            <UserSettingsMenuWidget />
            <UserAuthMenuWidget alwaysRound />
          </>
        }
        header={
          <GameInfo
            title="Быстрая партия"
            specs={gameSpecs}
            isRating={game?.noRatingChange === false}
            mobileBackLink={customBackUrl ?? BACK_LINK}
            onMobileBackClick={onClosePage}
            twoAvatars
          />
        }
        errorToast={toast}
        popup={
          showDrawOffer && fg.playerId ? (
            <DrawOffer onAccept={onDrawAccept} onDecline={onDrawDecline} />
          ) : undefined
        }
        player1={
          <PlayerPanelWidget
            orientation="top"
            color={player1Color}
            game={game}
            mobile={mobile}
            timePressureSec={TIME_PRESSURE}
            scoresheetTimeMs={customPlayersTimeMs}
            isFirstMoveTime={isTopFirstMove}
            networkProblem={isPlayer1Offline}
          />
        }
        player2={
          <PlayerPanelWidget
            orientation="bottom"
            color={player2Color}
            game={game}
            mobile={mobile}
            timePressureSec={TIME_PRESSURE}
            scoresheetTimeMs={customPlayersTimeMs}
            isFirstMoveTime={isBottomFirstMove}
            networkProblem={isPlayer2Offline}
          />
        }
        scoresheet={
          <Scoresheet
            {...scoresheet.props}
            enableGlobalShortcuts
            customMessage={rematchStatus === 'opponent' ? 'Соперник предлагает реванш…' : undefined}
          />
        }
        actions={
          <GameActions
            isPlaying={isPlayer && isInProgress}
            isFirstMove={isBottomFirstMove}
            isRating={!game.noRatingChange}
            canRequestDraw={canRequestDraw}
            canResign={canResign}
            canClaimVictory={canClaimVictory}
            canAnalyze={canShowAnalysis}
            canFindNew={canFindNew}
            newOpponentDisabled={matchmakingOpen}
            isSearching={Boolean(fg.selected.length)}
            canRematch={rematchStatus !== 'none'}
            rematchWaiting={rematchStatus === 'player'}
            rematchOpponentOffered={rematchStatus === 'opponent'}
            onRematch={onRematch}
            onCancelGame={fg.game?.reject}
            onDraw={onDrawOffer}
            onResign={onResign}
            onClaimVictory={onClaimVictory}
            onNewOpponent={handleNewOpponent}
            onNewSearch={handleNewSearch}
            onAnalize={onOpenAnalysis}
            onClose={onClosePage}
          />
        }
      />
    </>
  );
}
